import type { AnnotationData } from '../lib/types'

/** Unsaved edits of one annotation set and the revision they were made on (ANN-07). */
export interface Draft {
  revision: number
  data: AnnotationData
}

export interface Drafts {
  read(): Draft | undefined
  write(draft: Draft): void
  clear(): void
}

export const NO_DRAFTS: Drafts = { read: () => undefined, write: () => {}, clear: () => {} }

const PREFIX = 'pedfx.draft:'
/** How long a draft waits for its user to come back to the image; then it is dropped unsaved. */
export const DRAFT_DAYS = 30
const MAX_AGE = DRAFT_DAYS * 24 * 60 * 60 * 1000

function isDraft(value: unknown): value is Draft {
  const draft = value as Draft | null
  const data = draft?.data
  return typeof draft?.revision === 'number' && Array.isArray(data?.shapes) && Array.isArray(data.tags) && (data.comments === undefined || Array.isArray(data.comments))
}

/**
 * Drafts in the browser's storage, per user and image, so edits survive a closed tab or a crash
 * until they reach the server. Full or disabled storage only means no draft is kept.
 */
export function browserDrafts(owner: string, imageId: string, storage: Storage = localStorage): Drafts {
  const key = `${PREFIX}${owner}:${imageId}`
  return {
    read() {
      try {
        const value: unknown = JSON.parse(storage.getItem(key) ?? 'null')
        return isDraft(value) ? value : undefined
      } catch {
        return undefined
      }
    },
    write(draft) {
      try {
        storage.setItem(key, JSON.stringify({ ...draft, savedAt: Date.now() }))
      } catch {
        // storage full or disabled
      }
    },
    clear() {
      try {
        storage.removeItem(key)
      } catch {
        // storage disabled
      }
    },
  }
}

/**
 * Drops the drafts nobody came back for within DRAFT_DAYS, so edits never sent do not stay in the
 * browser for good. A draft written before drafts were dated starts its time now.
 */
export function dropStaleDrafts(storage: Storage = localStorage, now = Date.now()): void {
  try {
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index))
    for (const key of keys) {
      if (!key?.startsWith(PREFIX)) continue
      let value: (Draft & { savedAt?: number }) | null = null
      try {
        value = JSON.parse(storage.getItem(key) ?? 'null')
      } catch {
        // not a draft
      }
      if (!isDraft(value) || now - (value.savedAt ?? now) > MAX_AGE) storage.removeItem(key)
      else if (value.savedAt === undefined) storage.setItem(key, JSON.stringify({ ...value, savedAt: now }))
    }
  } catch {
    // storage disabled
  }
}
